import "./Navigation.scss";

import { useState, useContext, useId } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";

import WhiteSanaLogo from 'lib/images/primary-logo-white.svg';
import CancelIcon from "./icons/cancel.svg";
import DashboardIcon from "./icons/dashboard.svg";
import SanaPrimaryCareIcon from "./icons/sana-primary-care.svg";
import FindCareIcon from "./icons/find-care.svg";
import EmployeesIcon from "./icons/employees.svg";
import BenefitPlansIcon from "./icons/benefit-plans.svg";
import StopLossIcon from "./icons/stop-loss.svg";
import BillingHistoryIcon from "./icons/billing-history.svg";
import PermissionsIcon from "./icons/permissions.svg";
import MedicalIcon from "./icons/medical.svg";
import CarePartnersIcon from "./icons/care-partners.svg";
import DentalIcon from "./icons/dental.svg";
import VisionIcon from "./icons/vision.svg";
import OtherIcon from "./icons/other.svg";
import MedicalClaimsIcon from "./icons/medical-claims.svg";
import SubmitClaimIcon from "./icons/submit-a-claim.svg";
import HelpCenterIcon from "./icons/help-center.svg";
import DocumentsIcon from "./icons/documents.svg";
import MoreIcon from "icons/global/outline_24px/more_24px.svg";
import PortalSwitcherMenu from "./components/PortalSwitcherMenu";
import CompanySwitcher from "./components/CompanySwitcher";
import CompanySwitcherModal from "./components/CompanySwitcherModal";
import SettingsAndProfileMenu from "./components/SettingsAndProfileMenu";

import { MasterContext } from "master_context";

export default function Navigation({
  bundleName,
  handleToggleNavigationVisible,
  isAdmin,
  isBroker,
  isMember,
  visible,
  offerings,
  currentCompany,
  adminCompanies,
  permissions,
  firstName,
  lastName,
  profileName,
}) {

  const { PopoverContext } = useContext(MasterContext);

  const { triggerPopover, clearPopover } = PopoverContext;

  const [showCompanySwitcher, setShowCompanySwitcher] = useState(false);

  const renderBrokerLinks = () => {
    return (
      <div className="Navigation__link-group">
        <NavLink text="My Groups" route="/broker/dashboard" Icon={DashboardIcon} />
      </div>
    );
  };

  const renderCompanyAdminLinks = () => {
    const hasEditAdminPermission = permissions?.editAdminPermissions;

    return (
      <div className="Navigation__admin-links">
        <div className="Navigation__link-group">
          <NavLink text="Dashboard" route="/company/dashboard" Icon={DashboardIcon} dataTestId="admin-dashboard" />
          <NavLink text="Employees" route="/members" Icon={EmployeesIcon} dataTestId="employees"/>
          <NavLink text="Benefit Plans" route="/insurance-plans/medical" Icon={BenefitPlansIcon} dataTestId="benefit-plans"/>
          <NavLink text="Stop-loss" route="/reinsurance-policies" Icon={StopLossIcon } dataTestId="stop-loss"/>
          <NavLink text="Billing" route="/invoices" Icon={BillingHistoryIcon} dataTestId="billing-history"/>
          {hasEditAdminPermission && <NavLink text="Permissions" route="/permissions" Icon={PermissionsIcon} dataTestId="permissions"/>}
        </div>
      </div>
    );
  };

  const renderMemberLinks = () => {
    return (
      <div className="Navigation__member-links">
        <div className="Navigation__link-group">
          <NavLink text="Home" route="/dashboard" Icon={DashboardIcon} dataTestId="home"/>
          {offerings.includes("sana_care_visible") && (
            <NavLink text="Sana Care" route="/care" Icon={SanaPrimaryCareIcon} dataTestId="sana-care"/>
          )}
        </div>

        <hr className="Navigation__divider"/>

        <div className="Navigation__link-group">
          <span className="Navigation__link-group-header">Benefits</span>

          <NavLink text="Medical" route="/benefits/medical" Icon={MedicalIcon} dataTestId="medical"/>
          <NavLink text="Care Partners" route="/benefits/care-partners" Icon={CarePartnersIcon} dataTestId="care-partners"/>
          <NavLink text="Provider Directory" route="/find-a-doctor" Icon={FindCareIcon} dataTestId="provider-directory"/>
          {offerings.includes("dental") && (
            <NavLink text="Dental" route="/benefits/dental" Icon={DentalIcon} dataTestId="dental"/>
          )}
          {offerings.includes("vision") && (
            <NavLink text="Vision" route="/benefits/vision" Icon={VisionIcon} dataTestId="vision"/>
          )}
          {offerings.includes("life_and_disability") && (
            <NavLink text="Other" route="/benefits/other-benefits" Icon={OtherIcon} dataTestId="other"/>
          )}
        </div>

        <hr className="Navigation__divider"/>

        <div className="Navigation__link-group">
          <span className="Navigation__link-group-header">Claims</span>

          <NavLink text="Medical Claims" route="/my-claims/list" Icon={MedicalClaimsIcon} dataTestId="medical-claims"/>
          <NavLink text="Submit a Claim" route="/my-claims/submit" Icon={SubmitClaimIcon} dataTestId="submit-a-claim"/>
        </div>
      </div>
    );
  };

  const renderNavigationLinks = () => {
    switch (bundleName) {
      case 'CompanyAdminApp':
        return renderCompanyAdminLinks();
      case 'BrokerApp':
        return renderBrokerLinks();
      default:
        return renderMemberLinks();
    }
  };

  const DOCUMENT_PATH_ROOT = {
    "MemberPortalApp": "/member",
    "OpenEnrollmentApp": "/member",
    "CompanyAdminApp": "/company",
    "BrokerApp": "/broker",
  }

  // eslint-disable-next-line no-bitwise
  const hasMultipleRoles = ((isAdmin ^ isBroker) ? isMember : isAdmin);

  const showSettingsAndProfileMenu = () => {
    if (visible) return;

    triggerPopover(
      <SettingsAndProfileMenu isMember={isMember} />
    );
  }

  const showPortalSwitcherMenu = () => {
    triggerPopover(
      <PortalSwitcherMenu
        activeApp={bundleName}
        isAdmin={isAdmin}
        isBroker={isBroker}
        isMember={isMember}
        menuTitle={menuTitle}
      />
    );
  }

  const handleShowNavMenuPopoverOnKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;

    showPortalSwitcherMenu();
  }

  const hasMultipleCompanies = adminCompanies?.length > 1;

  const menuTitle = Object.freeze({
    "BrokerApp": "Broker View",
    "CompanyAdminApp": "Admin View",
    "MemberPortalApp": "Member View",
  });

  return (
    <div className={classnames("Navigation__overlay", {"Navigation__overlay--visible": visible})}>
      <nav className={classnames("Navigation", {"Navigation--visible": visible})} aria-label="Primary">
        <div className="Navigation__background">
          <div className="Navigation__container">
            <a
              href="/"
              className="Navigation__header-logo"
              data-testid="Navigation__header-logo"
              title="Sana Primary Logo"
            >
              <WhiteSanaLogo />
            </a>

            <div className="Navigation__menus">
              {hasMultipleRoles &&
                <div
                  className={
                    classnames(
                      "Navigation__nav-view-switcher",
                      {"Navigation__nav-view-switcher-multiple-companies" : hasMultipleCompanies && bundleName === "CompanyAdminApp"},
                    )
                  }
                  role="button"
                  tabIndex="0"
                  onClick={showPortalSwitcherMenu}
                  onKeyDown={handleShowNavMenuPopoverOnKeyDown}>
                  <div className="Navigation__nav-view-switcher-text">{menuTitle[bundleName]}</div>
                  <div className="Navigation__nav-view-switcher-icon">
                    <MoreIcon role="img" aria-labelledby="Switch View" />
                  </div>
                </div>
              }

              {currentCompany && (
                <CompanySwitcher
                  currentCompany={currentCompany}
                  adminCompanies={adminCompanies}
                  handleToggleCompanySwitcher={() => setShowCompanySwitcher(true)}
                />
              )}

              { (hasMultipleRoles || currentCompany) && <hr className="Navigation__divider"/> }
            </div>

            { renderNavigationLinks() }

            <hr className="Navigation__divider"/>

            <div className="Navigation__link-group">
              <span className="Navigation__link-group-header">Resources</span>
                <NavLink
                  text="Documents"
                  route={`${DOCUMENT_PATH_ROOT[bundleName]}/documents`}
                  Icon={DocumentsIcon}
                  dataTestId="documents"
                />
                <NavLink
                  text="Help Center"
                  route="//sanabenefits.zendesk.com/hc/en-us"
                  target="_blank"
                  rel="noreferrer"
                  Icon={HelpCenterIcon}
                  dataTestId="help-center"
                />
            </div>
            <div className="Navigation__mobile-controls">
              <hr className="Navigation__divider"/>
                <div className="Navigation__mobile-links">
                  { isMember &&  <NavLink text="Settings & Profile" route="/account-settings"/> }

                  <NavLink
                    text="Terms"
                    target="_blank"
                    rel="noreferrer"
                    route="https://sanabenefits.com/terms-of-service"
                  />

                  <NavLink
                    text="Privacy"
                    target="_blank"
                    rel="noreferrer"
                    route="https://sanabenefits.com/privacy-policy"
                  />

                  <NavLink
                    text="Log out"
                    route="/logout"
                  />
              </div>
            </div>

            { showCompanySwitcher &&
                <CompanySwitcherModal
                companies={adminCompanies}
                visible={showCompanySwitcher}
                onClose={() => setShowCompanySwitcher(false)}
              />
            }
          </div>
        </div>
        <div  className="Navigation__user-menu-container">
          <div className="Navigation__user-menu" role="button" onClick={showSettingsAndProfileMenu}>
            <div className="Navigation__avatar-container">
              <div className="Navigation__avatar">
                {firstName[0]}{lastName[0]}
              </div>
              {profileName}
            </div>
            <div className="Navigation__more-button">
              <MoreIcon />
            </div>
            <div
              className="Navigation__header-cancel"
              onClick={() => handleToggleNavigationVisible() }
              data-testid="Navigation__header">
                <CancelIcon />
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}

const NavLink = ({text, route, Icon, target, rel, dataTestId}) => {
  const isActive = window.location.pathname === route;

  return (
    <a
      href={route}

      target={target || "_self"}
      {...(rel ? { rel } : {})}
      className={classnames("Navigation__link", { "Navigation__link--active": isActive })}
      data-testid={dataTestId}>
        { Icon && <Icon id={useId()} className="Navigation__icon" /> }
        {text}
    </a>
  );
};

Navigation.defaultProps = {
  offerings: [],
}

Navigation.propTypes = {
  bundleName: PropTypes.string,
  handleToggleNavigationVisible: PropTypes.func,
  isAdmin: PropTypes.bool,
  isBroker: PropTypes.bool,
  isMember: PropTypes.bool,
  visible: PropTypes.bool,
  offerings: PropTypes.arrayOf(PropTypes.string),
  currentCompany: PropTypes.object,
  adminCompanies: PropTypes.arrayOf(PropTypes.object),
  permissions: PropTypes.object,
};
